import * as sharedActions from '../../shared/actions';
import * as svsActions from './actions';
import * as overviewActions from '../overview/actions';

import { DetailsSvsActionTypes, DetailsSvsState } from './types';
import { SvsViews } from './model';
import { SvsDTO as SvsResponse } from '../../../business/models/dto/svs/svs.dto';
import { initialCurrentLine, initialState } from './initial-state';
import { mapPolicyBusinessToSvs, mapSvsListResponseToSvs, mapSvsResponseToSvs } from './mapper';

import { ActionType } from 'typesafe-actions';
import { Reducer } from 'redux';
import { SharedActionTypes } from '../../shared/types';
import { getI18n } from '../../utils/i18n/strings.manager';
import { OverviewActionTypes } from '../overview/types';

export type SvsAction = ActionType<typeof svsActions>;
export type SharedAction = ActionType<typeof sharedActions>;
export type OverviewAction = ActionType<typeof overviewActions>;

const localization = getI18n();

const reducer: Reducer<DetailsSvsState, SvsAction | SharedAction | OverviewAction> = (state = initialState, action): DetailsSvsState => {
  switch (action.type) {
    case DetailsSvsActionTypes.CHANGE_VIEW: {
      return { ...state, previousView: state.currentView, currentView: action.payload.view };
    }
    case DetailsSvsActionTypes.TAB_VALUE_AND_CAUSE_FIELD_CHANGE: {
      return { ...state, valueAndCause: action.payload };
    }
    case DetailsSvsActionTypes.TAB_COMMUNICATION_FIELD_CHANGE: {
      const updatedTabs = state.tabs.map((tab, index) =>
        index === SvsViews.communication ? { ...tab, isDirty: true } : tab
      );
      return { ...state, communication: action.payload, tabs: updatedTabs };
    }
    case DetailsSvsActionTypes.TAB_VAT_AND_PAYMENT_FIELD_CHANGE: {
      return { ...state, vatAndPayment: action.payload };
    }
    case DetailsSvsActionTypes.TAB_COUNTER_EXPERTISE_FIELD_CHANGE: {
      const updatedTabs = state.tabs.map((tab, index) =>
        index === SvsViews.counterExpertise ? { ...tab, isDirty: true } : tab
      );
      return { ...state, counterExpertise: action.payload, tabs: updatedTabs };
    }
    case DetailsSvsActionTypes.TAB_REMARK_FIELD_CHANGE: {
      const remarksSelected = (!state.remark.remarksSelected && state.remark.remark === '') ? true : action.payload.remarksSelected;
      const updatedTabs = state.tabs.map((tab, index) =>
        index === SvsViews.remark ? { ...tab, isDirty: true } : tab
      );

      return { ...state, remark: { ...action.payload, remarksSelected: remarksSelected }, tabs: updatedTabs };
    }
    case DetailsSvsActionTypes.TAB_SIGNING_FIELD_CHANGE: {
      const updatedTabs = state.tabs.map((tab, index) =>
        index === SvsViews.signing ? { ...tab, isDirty: true } : tab
      );
      return { ...state, signing: action.payload, tabs: updatedTabs };
    }
    case DetailsSvsActionTypes.TAB_LINES_FIELD_CHANGE: {
      return {
        ...state, currentLine: {
          ...state.currentLine,
          description: action.payload.description,
          amount: action.payload.amount
        }
      };
    }
    case DetailsSvsActionTypes.TAB_LINES_ADD_LINE: {
      const newLine = {
        id: 0, amount: state.currentLine.amount, description: state.currentLine.description,
        sequence: state.lines.length + 1,
        svsHeaderID: state.id,
        lineFormatCode: '',
        deleted: false,
        isImportFromDacca: false
      };

      const newLinesArray = [...state.lines, newLine];
      const updatedTabs = [...state.tabs];
      updatedTabs[SvsViews.svsLines] = { ...updatedTabs[SvsViews.svsLines], isDirty: true };

      return {
        ...state, tabs: updatedTabs, lines: newLinesArray, lineAmountTotal: state.lineAmountTotal + +newLine.amount
      };
    }
    case DetailsSvsActionTypes.TAB_LINES_REMOVE_LINE: {
      const newLinesArray = state.lines.map(line =>
        line.sequence === action.payload
          ? { ...line, deleted: true }
          : line
      );
    
      const updatedTabs = state.tabs.map((tab, index) =>
        index === SvsViews.svsLines ? { ...tab, isDirty: true } : tab
      );
    
      const newTotal = newLinesArray.reduce(
        (total, line) => !line.deleted ? total + (line.amount as number) : total,
        0
      );
    
      return {
        ...state,
        tabs: updatedTabs,
        lines: newLinesArray,
        lineAmountTotal: newTotal,
        currentLine: initialCurrentLine,
      };
    }
    case DetailsSvsActionTypes.TAB_LINES_SET_EXCESS_APPLICABLE: {
      const updatedTabs = [...state.tabs];
      updatedTabs[SvsViews.svsLines] = { ...updatedTabs[SvsViews.svsLines], isDirty: true };

      return { ...state, excessApplicableTC: action.payload, tabs: updatedTabs };
    }
    case DetailsSvsActionTypes.TAB_LINES_SET_UNDER_CONDITION: {
      const updatedTabs = [...state.tabs];
      updatedTabs[SvsViews.svsLines] = { ...updatedTabs[SvsViews.svsLines], isDirty: true };

      return { ...state, underConditionTC: action.payload, tabs: updatedTabs };
    }
    case DetailsSvsActionTypes.FETCH_SVS_SUCCESS: {
      return mapSvsResponseToSvs(state, action.payload);
    }
    case DetailsSvsActionTypes.FETCH_SVS_LIST_SUCCESS: {
      return mapSvsListResponseToSvs(state, action.payload);
    }
    case DetailsSvsActionTypes.FETCH_SVS: {
      if (action.payload.id === 0) {
        return {
          ...state, ...initialState,
          svsItems: [...state.svsItems], svsItemsTabs: { ...state.svsItemsTabs }
        };
      }

      return { ...state };
    }
    case DetailsSvsActionTypes.FETCH_SVS_ADDITIONAL_SUCCESS: {
      const { SVSApplicationOfTheExemptionTC, SVSSubjectToTC, ...additionalDataWithoutTCs } = action.payload;

      return {
        ...state,
        SVSApplicationOfTheExemptionTC: SVSApplicationOfTheExemptionTC,
        SVSSubjectToTC: SVSSubjectToTC,
        additionalData: { ...additionalDataWithoutTCs }
      };
    }
    case DetailsSvsActionTypes.SET_SVS_ID: {
      return { ...state, id: action.payload };
    }
    case DetailsSvsActionTypes.SET_SVS_LINES: {
      const typeActionPayload = action.payload as SvsResponse;
      return { ...state, lines: [...typeActionPayload.lines], SVSSubjectToTC: typeActionPayload.SVSSubjectToTC, SVSApplicationOfTheExemptionTC: typeActionPayload.SVSApplicationOfTheExemptionTC };
    }
    case DetailsSvsActionTypes.RESET_STATE: {
      return { ...state, ...initialState };
    }
    case DetailsSvsActionTypes.UPDATE_SIGNING: {
      const updatedTabs = [...state.tabs];
      updatedTabs[SvsViews.signing] = { ...updatedTabs[SvsViews.communication], isDirty: true };

      return { ...state, signing: { ...action.payload.signing }, tabs: [...updatedTabs] };
    }
    case DetailsSvsActionTypes.SET_TAB_AS_SAVED: {
      const updatedTabs = [...state.tabs];
      updatedTabs[action.payload] = { ...updatedTabs[action.payload], isDirty: true };

      return { ...state, tabs: updatedTabs };
    }
    case SharedActionTypes.BUSINESS_UPDATE: {
      if (action.payload) { return mapPolicyBusinessToSvs(state, action.payload); }
      return state;
    }
    case DetailsSvsActionTypes.SET_SVS_LOCKED: {
      return {
        ...state,
        locked: true
      };
    }
    case DetailsSvsActionTypes.SET_VALIDATED: {
      return {
        ...state,
        validated: action.payload
      };
    }
    case DetailsSvsActionTypes.SET_CURRENT_TAB_AS_DIRTY: {
      const updatedTabs = [...state.tabs];
      updatedTabs[state.currentView] = { ...updatedTabs[state.currentView], isDirty: true };

      return { ...state, tabs: updatedTabs };
    }
    case DetailsSvsActionTypes.IMPORT_LINES_FROM_DACCA_SUCCESS: {
      const deletedSvsLines = state.lines
        .filter(line => line.id !== 0)
        .map(line => ({ ...line, deleted: true }));
    
      const emptyLine = {
        id: 0,
        amount: 0,
        description: localization.get('details.svs.daccaLineDescription'),
        sequence: 1,
        svsHeaderID: state.id,
        lineFormatCode: '',
        deleted: false,
        isImportFromDacca: true,
      };
    
      const updatedTabs = state.tabs.map((tab, index) =>
        index === SvsViews.svsLines ? { ...tab, isDirty: true } : tab
      );
    
      return {
        ...state,
        tabs: updatedTabs,
        lines: [...deletedSvsLines, emptyLine],
        lineAmountTotal: 0,
        currentLine: initialCurrentLine,
      };
    }    
    case OverviewActionTypes.GENERATE_REPORTS_SVS_SUCCESS: {
      const updatedSvsList = state.svsItems.map(svs => {
        const update = action.payload.find(res => svs.id === res.svsHeaderId);
        return update ? { ...svs, svsData: { ...svs.svsData, documentAmazonKey: update.amazonKey } } : svs;
      });
    
      return { ...state, svsItems: updatedSvsList };
    }    
    default:
      return state;
  }
};

export { reducer as detailsSvsReducer, initialState as detailsSvsInitialState };
